/**
 * REST calls for feature requests (`/api/feature-requests/`).
 *
 * Requesters (feature_requests:create) submit and chat on their own requests;
 * developers (feature_requests:manage) work every request on the board.
 * Backend reference: wealth-builder-dev-v1-backend docs/feature_requests/API.md.
 */

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');
const BASE = '/api/feature-requests';

export const FEATURE_REQUESTS_PAGE_SIZE = 20;

export type FeatureRequestStatus =
  | 'submitted'
  | 'under_review'
  | 'planned'
  | 'in_progress'
  | 'completed'
  | 'declined';

/** Statuses a developer can move a request to with PATCH (closing needs complete/decline). */
export type OpenFeatureRequestStatus = Exclude<FeatureRequestStatus, 'completed' | 'declined'>;

export type FeatureRequestPriority = 'low' | 'medium' | 'high';

export interface FeatureRequestPerson {
  id: number;
  name: string;
}

export interface FeatureRequestAccess {
  /** feature_requests:create — may submit. */
  can_request: boolean;
  /** feature_requests:manage — works the developer board. */
  can_manage: boolean;
}

/** One message in the request's thread. `is_internal` is present on the developer side only. */
export interface FeatureRequestComment {
  id: number;
  author: FeatureRequestPerson | null;
  body: string;
  is_internal?: boolean;
  is_from_requester: boolean;
  /** `completed` / `declined` on the closing note, blank otherwise. */
  status_change: '' | 'completed' | 'declined';
  created_at: string;
}

export interface FeatureRequestListItem {
  request_number: string;
  title: string;
  area: string;
  status: FeatureRequestStatus;
  status_display: string;
  priority: FeatureRequestPriority;
  priority_display: string;
  requested_by: FeatureRequestPerson | null;
  assignee: FeatureRequestPerson | null;
  /** Unread for the developers. */
  needs_action: boolean;
  /** A developer wrote and the requester has not opened it since. */
  requester_unread: boolean;
  comment_count: number;
  picked_at: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FeatureRequestDetail extends FeatureRequestListItem {
  description: string;
  reported_by: FeatureRequestPerson | null;
  comments: FeatureRequestComment[];
}

export interface PaginatedFeatureRequests {
  count: number;
  next: string | null;
  previous: string | null;
  results: FeatureRequestListItem[];
}

export interface CreateFeatureRequestPayload {
  title: string;
  description: string;
  area?: string;
  priority: FeatureRequestPriority;
}

export interface FeatureBoardFilters {
  /** Comma list of statuses, or empty for all. */
  status: string;
  assignee: '' | 'me' | 'none';
  needsAction: boolean;
  q: string;
  page: number;
}

export interface FeatureBoardCounts {
  needs_action: number;
  unassigned: number;
  mine: number;
  by_status: Record<FeatureRequestStatus, number>;
}

/** Error from a feature request call, carrying the backend `code` (409s) when given. */
export class FeatureRequestApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
    this.name = 'FeatureRequestApiError';
  }
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('wb.authToken');
  if (!token) throw new Error('No authentication token found. Please sign in again.');
  return { Authorization: `Token ${token}`, 'Content-Type': 'application/json' };
}

/** Flatten a DRF error value (string | string[] | nested) to one message. */
function firstMessage(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const msg = firstMessage(item);
      if (msg) return msg;
    }
  }
  return null;
}

async function toApiError(response: Response, fallback: string): Promise<FeatureRequestApiError> {
  let payload: Record<string, unknown> | null = null;
  try {
    payload = (await response.json()) as Record<string, unknown>;
  } catch {
    return new FeatureRequestApiError(fallback, response.status);
  }
  const code = typeof payload?.code === 'string' ? payload.code : undefined;
  let message = firstMessage(payload?.detail);
  if (!message && payload) {
    for (const [key, value] of Object.entries(payload)) {
      const msg = firstMessage(value);
      if (msg) {
        message = key === 'non_field_errors' ? msg : `${key.replace(/_/g, ' ')}: ${msg}`;
        break;
      }
    }
  }
  return new FeatureRequestApiError(message || fallback, response.status, code);
}

async function request<T>(path: string, fallback: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${BASE}${path}`, { ...init, headers: authHeaders() });
  if (!response.ok) throw await toApiError(response, fallback);
  return (await response.json()) as T;
}

const post = <T>(path: string, body: unknown, fallback: string) =>
  request<T>(path, fallback, { method: 'POST', body: JSON.stringify(body) });

const enc = encodeURIComponent;

// -- access --------------------------------------------------------------------

export function getFeatureRequestAccess(signal?: AbortSignal): Promise<FeatureRequestAccess> {
  return request('/my-access/', 'Failed to check feature request access.', { signal });
}

// -- requester -----------------------------------------------------------------

export function fetchMyFeatureRequests(page: number, signal?: AbortSignal): Promise<PaginatedFeatureRequests> {
  const params = new URLSearchParams({ page: String(page), page_size: String(FEATURE_REQUESTS_PAGE_SIZE) });
  return request(`/my/requests/?${params}`, 'Failed to load your requests.', { signal });
}

export function fetchMyFeatureRequest(number: string, signal?: AbortSignal): Promise<FeatureRequestDetail> {
  return request(`/my/requests/${enc(number)}/`, 'Failed to load the request.', { signal });
}

export function createFeatureRequest(payload: CreateFeatureRequestPayload): Promise<FeatureRequestDetail> {
  return post('/my/requests/', payload, 'Failed to submit the request.');
}

/** Messages newer than `after` (0 = all). Reading clears the requester's unread flag. */
export function fetchMyThread(number: string, after: number, signal?: AbortSignal): Promise<FeatureRequestComment[]> {
  return request(`/my/requests/${enc(number)}/comments/?after=${after}`, 'Failed to load messages.', { signal });
}

export function sendMyMessage(number: string, body: string): Promise<FeatureRequestComment> {
  return post(`/my/requests/${enc(number)}/comments/`, { body }, 'Failed to send the message.');
}

// -- developer board -----------------------------------------------------------

export function fetchBoard(filters: FeatureBoardFilters, signal?: AbortSignal): Promise<PaginatedFeatureRequests> {
  const params = new URLSearchParams({ page: String(filters.page), page_size: String(FEATURE_REQUESTS_PAGE_SIZE) });
  if (filters.status) params.set('status', filters.status);
  if (filters.assignee) params.set('assignee', filters.assignee);
  if (filters.needsAction) params.set('needs_action', '1');
  if (filters.q.trim()) params.set('q', filters.q.trim());
  return request(`/admin/requests/?${params}`, 'Failed to load the board.', { signal });
}

export function fetchBoardRequest(number: string, signal?: AbortSignal): Promise<FeatureRequestDetail> {
  return request(`/admin/requests/${enc(number)}/`, 'Failed to load the request.', { signal });
}

export function fetchBoardThread(number: string, after: number, signal?: AbortSignal): Promise<FeatureRequestComment[]> {
  return request(`/admin/requests/${enc(number)}/comments/?after=${after}`, 'Failed to load messages.', { signal });
}

export function sendBoardMessage(number: string, body: string, isInternal: boolean): Promise<FeatureRequestComment> {
  return post(`/admin/requests/${enc(number)}/comments/`, { body, is_internal: isInternal }, 'Failed to send the message.');
}

export function updateBoardRequest(
  number: string,
  payload: { status?: OpenFeatureRequestStatus; priority?: FeatureRequestPriority; assignee_id?: number | null },
): Promise<FeatureRequestDetail> {
  return request(`/admin/requests/${enc(number)}/`, 'Failed to update the request.', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function pickBoardRequest(number: string): Promise<FeatureRequestDetail> {
  return post(`/admin/requests/${enc(number)}/pick/`, {}, 'Failed to pick up the request.');
}

export function closeBoardRequest(
  number: string,
  outcome: 'complete' | 'decline',
  note: string,
): Promise<FeatureRequestDetail> {
  return post(`/admin/requests/${enc(number)}/${outcome}/`, { note }, `Failed to ${outcome} the request.`);
}

export function fetchBoardCounts(signal?: AbortSignal): Promise<FeatureBoardCounts> {
  return request('/admin/counts/', 'Failed to load counts.', { signal });
}

export function fetchDevelopers(signal?: AbortSignal): Promise<FeatureRequestPerson[]> {
  return request('/admin/developers/', 'Failed to load developers.', { signal });
}
